import {
  Close,
  LockOutlined,
  LockOpenOutlined,
  WarningAmberRounded,
  InfoOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  alpha,
  Divider,
} from '@mui/material';
import { useState } from 'react';
import { toggleQaqcReportVisibilityApi } from '../../../../helpers/api';
import { openNotification } from '../../../../helpers/notification';

export default function ResultLock({ open, onClose, record, onRefreshPage }) {
  const [loading, setLoading] = useState(false);
  const isVisible = record?.isVisible;

  const handleLockUnlock = () => {
    setLoading(true);
    const payload = [record?.reportId];
    toggleQaqcReportVisibilityApi(payload)
      .then(() => {
        onClose();
        onRefreshPage();
        return openNotification({
          type: 'success',
          title: 'Update Successful',
          message: `The report visibility has been updated successfully.`,
        });
      })
      .catch((error) => {
        const errorMessage = error.data?.result?.status?.desc || 'Unable to update status';
        return openNotification({
          type: 'error',
          title: 'Something went wrong',
          message: `${errorMessage}`,
        });
      })
      .finally(() => setLoading(false));
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: { borderRadius: '20px', padding: 1 },
      }}
    >
      {/* Header Section */}
      <Box display="flex" alignItems="center" px={2} pt={1}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexGrow: 1 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: '12px',
              bgcolor: isVisible ? alpha('#ed6c02', 0.1) : alpha('#2e7d32', 0.1),
              color: isVisible ? '#ed6c02' : '#2e7d32',
              display: 'flex',
            }}
          >
            {isVisible ? <LockOutlined /> : <LockOpenOutlined />}
          </Box>
          <Typography variant="h6" fontWeight={700}>
            {isVisible ? 'Lock Report' : 'Unlock Report'}
          </Typography>
        </Stack>
        <IconButton onClick={onClose} size="small" sx={{ bgcolor: alpha('#000', 0.03) }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ mt: 2 }}>
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="body1" fontWeight={700}>
              {record?.reportTitle || 'Untitled Report'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Facility: {record?.facility || 'Unknown Facility'}
            </Typography>
          </Box>

          <Alert
            severity={isVisible ? 'warning' : 'info'}
            icon={isVisible ? <WarningAmberRounded /> : <InfoOutlined />}
            sx={{ borderRadius: '12px', fontWeight: 500 }}
          >
            {isVisible
              ? 'Locking this report will hide all collected QAQC data from facility personnel.'
              : 'Unlocking this report will grant facility personnel full access to view the results.'}
          </Alert>

          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{ fontStyle: 'italic' }}
          >
            Note: This visibility setting can be changed again at any time.
          </Typography>
        </Stack>
      </DialogContent>

      <Divider sx={{ mx: 3, my: 1, opacity: 0.5 }} />

      <DialogActions sx={{ padding: '1.5rem 2rem', justifyContent: 'flex-end' }}>
        <Button
          onClick={onClose}
          sx={{ textTransform: 'none', fontWeight: 700, color: 'text.secondary' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color={isVisible ? 'warning' : 'success'}
          sx={{
            textTransform: 'none',
            borderRadius: '10px',
            px: 4,
            fontWeight: 700,
            boxShadow: `0 8px 16px ${alpha(isVisible ? '#ed6c02' : '#2e7d32', 0.2)}`,
          }}
          onClick={handleLockUnlock}
          disabled={loading}
        >
          <Stack direction="row" spacing={1.2} alignItems="center">
            {loading ? <CircularProgress size={18} color="inherit" /> : null}
            <Typography fontWeight={700}>
              {isVisible ? 'Confirm Lock' : 'Confirm Unlock'}
            </Typography>
          </Stack>
        </Button>
      </DialogActions>
    </Dialog>
  );
}
